import { useMemo, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { Card, EarnRule } from "../lib/types";
import { formatINR } from "../lib/format";
import { Badge, EmptyState, ErrorBanner } from "./ui";
import { baseRuleOf, effectiveRate, formatMultiple, multipleOf } from "../lib/rules";

type Draft = Pick<
  EarnRule,
  "rate" | "unitAmount" | "multiplier" | "multiplierIncludesBase" | "capValue" | "capType" | "capPeriod" | "capAppliesTo" | "capOverflow" | "capGroup" | "priority" | "minAmount"
>;

/**
 * Every earn rule across every card, in one table.
 *
 * Reviewing rates a card at a time means opening a modal per rule; when an
 * issuer revises terms — or you're checking the seeded figures against your
 * actual card agreements — you want them all side by side. The figures that
 * change are editable in place; anything structural (categories, merchant
 * keywords, exclusions) stays in the per-rule form on the card itself.
 */
export function BulkRuleEditor({
  cards,
  categories,
  onSaved,
  onError,
}: {
  cards: Card[];
  categories: { key: string; label: string }[];
  onSaved: (count: number) => void;
  onError: (message: string) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const rows = useMemo(() => {
    const all = cards.flatMap((card) =>
      card.earnRules.map((rule) => ({ card, rule })),
    );
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      ({ card, rule }) =>
        card.name.toLowerCase().includes(q) ||
        rule.name.toLowerCase().includes(q) ||
        rule.categories.some((c) => c.toLowerCase().includes(q)) ||
        rule.merchantPatterns.some((m) => m.toLowerCase().includes(q)),
    );
  }, [cards, filter]);

  const valueOf = (rule: EarnRule, field: keyof Draft) =>
    drafts[rule.id]?.[field] ?? rule[field];

  // A draft starts as a copy of the stored rule, so saving sends a complete
  // rule rather than whichever fields happened to be touched.
  const edit = (rule: EarnRule, patch: Partial<Draft>) =>
    setDrafts((d) => {
      const base: Draft = d[rule.id] ?? {
        rate: rule.rate,
        unitAmount: rule.unitAmount,
        multiplier: rule.multiplier ?? null,
        multiplierIncludesBase: rule.multiplierIncludesBase ?? true,
        capValue: rule.capValue,
        capType: rule.capType,
        capPeriod: rule.capPeriod,
        capAppliesTo: rule.capAppliesTo ?? "ALL",
        capOverflow: rule.capOverflow ?? "BASE",
        capGroup: rule.capGroup ?? null,
        priority: rule.priority,
        minAmount: rule.minAmount,
      };
      return { ...d, [rule.id]: { ...base, ...patch } };
    });

  const dirtyIds = Object.keys(drafts);

  async function saveAll() {
    setSaving(true);
    setProblem(null);
    let saved = 0;
    try {
      for (const id of dirtyIds) {
        const found = rows.find(({ rule }) => rule.id === id) ?? cards.flatMap((c) => c.earnRules.map((r) => ({ card: c, rule: r }))).find((x) => x.rule.id === id);
        if (!found) continue;
        await api.put(`/cards/${found.card.id}/rules/${id}`, drafts[id]);
        saved++;
      }
      setDrafts({});
      onSaved(saved);
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : "Could not save the changes");
      onError(err instanceof ApiError ? err.message : "Could not save the changes");
    } finally {
      setSaving(false);
    }
  }

  if (cards.length === 0) {
    return <EmptyState title="No cards yet" hint="Add a card and its rules will show up here." />;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[16rem] flex-1">
          <label className="label" htmlFor="rule-filter">Filter</label>
          <input
            id="rule-filter"
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Card, rule, category or merchant…"
          />
        </div>
        <div className="flex items-center gap-3">
          {dirtyIds.length > 0 && (
            <>
              <span className="text-xs text-amber-700">{dirtyIds.length} unsaved</span>
              <button className="btn-secondary" onClick={() => setDrafts({})} disabled={saving}>Discard</button>
            </>
          )}
          <button className="btn-primary" onClick={saveAll} disabled={saving || dirtyIds.length === 0}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {problem && <ErrorBanner message={problem} onDismiss={() => setProblem(null)} />}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-200">
              <th className="th">Card</th>
              <th className="th">Rule</th>
              <th className="th">Applies to</th>
              <th className="th text-right" title="The total the rule earns, base included — not the extra on top">Total pts (incl. base)</th>
              <th className="th text-right">Per ₹</th>
              <th className="th">Cap</th>
              <th className="th text-right">Priority</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {rows.map(({ card, rule }) => {
              const dirty = drafts[rule.id] != null;
              return (
                <tr key={rule.id} className={dirty ? "bg-amber-50/60" : undefined}>
                  <td className="td">
                    {/* A flex row with min-w-0 is what actually lets the name
                        truncate; max-width on the cell alone does not. */}
                    <div className="flex w-[8rem] items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: card.color }} aria-hidden />
                      <span className="min-w-0 truncate text-ink-700" title={card.name}>{card.name}</span>
                    </div>
                  </td>
                  <td className="td min-w-[8rem]">
                    <span className="font-medium text-ink-900">{rule.name}</span>
                    {rule.isExclusion && <Badge tone="critical">Earns nothing</Badge>}
                    {(rule.validFrom || rule.validUntil) && (
                      <span className="block text-xs text-ink-500">
                        {rule.validFrom ? `from ${rule.validFrom.slice(0, 10)}` : ""}
                        {rule.validFrom && rule.validUntil ? " " : ""}
                        {rule.validUntil ? `until ${rule.validUntil.slice(0, 10)}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="td max-w-[11rem] truncate text-xs text-ink-500" title={scopeOf(rule, categories)}>
                    {scopeOf(rule, categories)}
                  </td>
                  {rule.multiplier != null ? (
                    <>
                      <td className="td text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input
                            className="input tnum w-[3.5rem] px-1.5 py-1 text-right text-sm"
                            type="number"
                            min={0}
                            step="0.5"
                            value={String(valueOf(rule, "multiplier") ?? "")}
                            onChange={(e) => edit(rule, { multiplier: e.target.value === "" ? null : Number(e.target.value) })}
                            aria-label={`Multiple of the base rate for ${rule.name} on ${card.name}`}
                          />
                          <span className="text-sm text-ink-600">X</span>
                        </div>
                      </td>
                      <td className="td">
                        <select
                          className="input w-[7rem] px-1.5 py-1 text-sm"
                          value={valueOf(rule, "multiplierIncludesBase") === false ? "ONTOP" : "INCL"}
                          onChange={(e) => edit(rule, { multiplierIncludesBase: e.target.value === "INCL" })}
                          aria-label={`Whether ${rule.name}'s multiple includes the base`}
                        >
                          <option value="INCL">incl. base</option>
                          <option value="ONTOP">on top of base</option>
                        </select>
                        <span className="block text-xs text-ink-500">
                          {(() => {
                            const r = effectiveRate(
                              { ...rule, multiplier: valueOf(rule, "multiplier") as number | null, multiplierIncludesBase: valueOf(rule, "multiplierIncludesBase") as boolean },
                              baseRuleOf(card.earnRules),
                            );
                            return `= ${r.rate} / ₹${r.unitAmount}`;
                          })()}
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                  <td className="td text-right">
                    <input
                      className="input tnum w-[4.5rem] px-1.5 py-1 text-right text-sm"
                      type="number"
                      min={0}
                      step="0.1"
                      disabled={rule.isExclusion}
                      value={rule.isExclusion ? "" : String(valueOf(rule, "rate"))}
                      onChange={(e) => edit(rule, { rate: Number(e.target.value) })}
                      aria-label={`Points for ${rule.name} on ${card.name}`}
                    />
                    {!rule.isExclusion && (() => {
                      const base = baseRuleOf(card.earnRules);
                      if (!base || base.id === rule.id) return null;
                      const m = multipleOf(
                        { rate: Number(valueOf(rule, "rate")), unitAmount: Number(valueOf(rule, "unitAmount")) },
                        base,
                      );
                      return m !== null && Math.abs(m - 1) > 1e-9 ? (
                        <span className="block text-xs text-ink-500">{formatMultiple(m)}</span>
                      ) : null;
                    })()}
                  </td>
                  <td className="td text-right">
                    <input
                      className="input tnum w-[4.5rem] px-1.5 py-1 text-right text-sm"
                      type="number"
                      min={1}
                      disabled={rule.isExclusion}
                      value={rule.isExclusion ? "" : String(valueOf(rule, "unitAmount"))}
                      onChange={(e) => edit(rule, { unitAmount: Number(e.target.value) })}
                      aria-label={`Spend unit for ${rule.name} on ${card.name}`}
                    />
                  </td>
                    </>
                  )}
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <select
                        className="input w-[4.5rem] px-1.5 py-1 text-sm"
                        disabled={rule.isExclusion}
                        value={String(valueOf(rule, "capType"))}
                        onChange={(e) => edit(rule, { capType: e.target.value })}
                        aria-label={`Cap type for ${rule.name}`}
                      >
                        <option value="NONE">None</option>
                        <option value="POINTS">Points</option>
                        <option value="SPEND">Spend</option>
                      </select>
                      <input
                        className="input tnum w-[5.5rem] px-1.5 py-1 text-right text-sm"
                        type="number"
                        min={0}
                        disabled={rule.isExclusion || valueOf(rule, "capType") === "NONE"}
                        value={valueOf(rule, "capValue") == null ? "" : String(valueOf(rule, "capValue"))}
                        onChange={(e) => edit(rule, { capValue: e.target.value === "" ? null : Number(e.target.value) })}
                        aria-label={`Cap value for ${rule.name}`}
                      />
                      <select
                        className="input w-[5.5rem] px-1.5 py-1 text-sm"
                        disabled={rule.isExclusion || valueOf(rule, "capType") === "NONE"}
                        value={String(valueOf(rule, "capPeriod"))}
                        onChange={(e) => edit(rule, { capPeriod: e.target.value })}
                        aria-label={`Cap period for ${rule.name}`}
                      >
                        <option value="MONTH">/ month</option>
                        <option value="STATEMENT">/ statement</option>
                        <option value="QUARTER">/ quarter</option>
                        <option value="YEAR">/ year</option>
                      </select>
                    </div>
                    {valueOf(rule, "capType") !== "NONE" && !rule.isExclusion && (
                      <div className="mt-1 flex items-center gap-1">
                        <select
                          className="input w-[9.5rem] px-1.5 py-1 text-sm"
                          value={
                            valueOf(rule, "capAppliesTo") === "BONUS"
                              ? "BONUS"
                              : valueOf(rule, "capOverflow") === "NOTHING"
                                ? "NOTHING"
                                : "BASE"
                          }
                          onChange={(e) =>
                            edit(rule, {
                              capAppliesTo: e.target.value === "BONUS" ? "BONUS" : "ALL",
                              capOverflow: e.target.value === "NOTHING" ? "NOTHING" : "BASE",
                            })
                          }
                          aria-label={`What happens at the cap for ${rule.name}`}
                        >
                          <option value="BONUS">bonus stops</option>
                          <option value="BASE">then base rate</option>
                          <option value="NOTHING">then nothing</option>
                        </select>
                        <input
                          className="input w-[6.5rem] px-1.5 py-1 text-sm"
                          value={String(valueOf(rule, "capGroup") ?? "")}
                          onChange={(e) => edit(rule, { capGroup: e.target.value || null })}
                          placeholder="shared cap"
                          aria-label={`Shared cap name for ${rule.name}`}
                        />
                      </div>
                    )}
                  </td>
                  <td className="td text-right">
                    <input
                      className="input tnum w-16 px-2 py-1 text-right text-sm"
                      type="number"
                      value={String(valueOf(rule, "priority"))}
                      onChange={(e) => edit(rule, { priority: Number(e.target.value) })}
                      aria-label={`Priority for ${rule.name}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && <p className="py-8 text-center text-sm text-ink-500">Nothing matches that filter.</p>}

      <p className="mt-4 text-xs text-ink-500">
        Rates, units, caps and priorities are editable here, and a change here applies to every date. When a bank
        changes its terms from a date, use <strong>Edit</strong> on the card's rule instead and tick "These changes
        start on a date", so spends before it keep the old terms. Categories, merchant keywords and exclusions stay on
        the card's own rule form. After saving, run <strong>Recalculate points</strong> on the Transactions page to
        re-score your history.
      </p>
    </div>
  );
}

function scopeOf(rule: EarnRule, categories: { key: string; label: string }[]): string {
  const parts: string[] = [];
  if (rule.categories.length > 0) {
    parts.push(rule.categories.map((k) => categories.find((c) => c.key === k)?.label ?? k).join(", "));
  }
  if (rule.merchantPatterns.length > 0) parts.push(rule.merchantPatterns.join(", "));
  if (rule.channel !== "ANY") parts.push(rule.channel === "ONLINE" ? "online" : "in store");
  if (rule.minAmount) parts.push(`min ${formatINR(rule.minAmount)}`);
  return parts.length > 0 ? parts.join(" · ") : "Everything (base rate)";
}
